// src/pages/KategoriPage.tsx
import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { postRequest } from "../../../api/postRequest";
import Select, { FormatOptionLabelMeta, GroupBase } from "react-select";

/* =============================
   Tipler
============================= */
interface ApiResponse<T> {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
}

interface Category {
  id: number;
  ad: string;
  ustKategoriId: number | null;
  slug: string;
  siraNo: number;
  aktifMi: boolean;
}

interface TreeCategory extends Category {
  children: TreeCategory[];
}
interface RowItem {
  cat: Category;
  depth: number;
}

interface KategoriAddRequest {
  ad: string;
  ustKategoriId: number | null;
  slug: string;
  siraNo: number;
}
interface KategoriUpdateRequest extends KategoriAddRequest {
  id: number;
  aktifMi: boolean;
}
interface KategoriSilRequest {
  id: number;
}

interface CategoryOption {
  value: number;
  label: string;
  depth: number;
}

/* =============================
   Sayfa
============================= */
const KategoriPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<Partial<Category>>({
    ad: "",
    ustKategoriId: null,
    slug: "",
    siraNo: 0,
    aktifMi: true,
  });
  const [manualSlug, setManualSlug] = useState(false);

  // listele
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await postRequest<{}, ApiResponse<Category[]>>("/Kategori/listele", {});
      if (res.success && res.data) setCategories(res.data);
      else alert(res.errorDetail || "Listeleme hatası");
    } catch {
      alert("Sunucu hatası oldu");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCategories(); }, []);

  // modal aç/kapat
  const openAddModal = () => {
    setIsEditing(false);
    setForm({ ad: "", ustKategoriId: null, slug: "", siraNo: 0, aktifMi: true });
    setManualSlug(false);
    setModalOpen(true);
  };
  const openEditModal = (cat: Category) => {
    setIsEditing(true);
    setForm({ ...cat });
    setManualSlug(true); // düzenlemede slug’ı elle bırak
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  // ad değişince otomatik slug
  useEffect(() => {
    if (!manualSlug && form.ad !== undefined) {
      const generated = form.ad
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9ğüşöçıİĞÜŞÖÇ ]+/g, "")
        .replace(/\s+/g, "-");
      setForm((f) => ({ ...f, slug: generated }));
    }
  }, [form.ad, manualSlug]);

  // kaydet
  const handleSave = async () => {
    if (!form.ad?.trim()) {
      alert("Lütfen kategori adını girin.");
      return;
    }
    try {
      if (isEditing) {
        const payload: KategoriUpdateRequest = {
          id: form.id!,
          ad: form.ad!,
          ustKategoriId: form.ustKategoriId ?? null,
          slug: form.slug || "",
          siraNo: form.siraNo ?? 0,
          aktifMi: !!form.aktifMi,
        };
        const res = await postRequest<KategoriUpdateRequest, ApiResponse<string>>("/Kategori/guncelle", payload);
        if (!res.success) throw new Error(res.errorDetail);
      } else {
        const payload: KategoriAddRequest = {
          ad: form.ad!,
          ustKategoriId: form.ustKategoriId ?? null,
          slug: form.slug || "",
          siraNo: form.siraNo ?? 0,
        };
        const res = await postRequest<KategoriAddRequest, ApiResponse<string>>("/Kategori/ekle", payload);
        if (!res.success) throw new Error(res.errorDetail);
      }
      closeModal();
      fetchCategories();
    } catch (err: any) {
      alert(err.message || "Kaydetme sırasında hata oluştu.");
    }
  };

  // sil
  const handleDelete = async (id: number) => {
    if (!window.confirm("Bu kategoriyi silmek istediğine emin misin?")) return;
    try {
      const res = await postRequest<KategoriSilRequest, ApiResponse<string>>("/Kategori/sil", { id });
      if (!res.success) throw new Error(res.errorDetail);
      fetchCategories();
    } catch (err: any) {
      alert(err.message || "Silme sırasında hata oluştu.");
    }
  };

  /* =============================
     Ağaç -> Düz liste (depth’li)
  ============================= */
  const buildTree = (list: Category[]): TreeCategory[] => {
    const map = new Map<number, TreeCategory>();
    const roots: TreeCategory[] = [];
    list.forEach((c) => map.set(c.id, { ...c, children: [] }));
    map.forEach((node) => {
      if (node.ustKategoriId && map.has(node.ustKategoriId)) {
        map.get(node.ustKategoriId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });
    return roots;
  };
  const flattenTree = (nodes: TreeCategory[], depth = 0): RowItem[] =>
    nodes.flatMap((n) => [{ cat: n, depth }, ...flattenTree(n.children, depth + 1)]);

  const rows: RowItem[] = flattenTree(buildTree(categories));
  const flat = rows; // aynı içerik

  const categoryOptions: CategoryOption[] = flat.map(({ cat, depth }) => ({
    value: cat.id,
    label: cat.ad,
    depth,
  }));

  /* =============================
     UI
  ============================= */
  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Başlık kartı */}
      <div className="bg-white border rounded-lg shadow-sm p-3 sm:p-3.5 md:p-4 flex items-center justify-between">
        <h1 className="text-lg sm:text-xl md:text-2xl font-normal ">Kategori Yönetimi</h1>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-green-600 text-white px-3 sm:px-4 py-2 rounded hover:bg-green-700 text-sm"
        >
          <Plus className="w-4 h-4" /> Yeni Kategori
        </button>
      </div>

      {/* Mobil kart liste (md:hidden) */}
      <div className="space-y-2 md:hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Kayıt yok</div>
        ) : (
          rows.map(({ cat, depth }) => (
            <div key={cat.id} className="bg-white border rounded-lg shadow-sm p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div
                    className="font-medium truncate"
                    style={{ paddingLeft: `${(depth ? depth : 0) * 12}px` }}
                  >
                    {cat.ad}
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                      <div className="text-gray-500">Üst Kategori</div>
                      <div className="font-medium">
                        {cat.ustKategoriId
                          ? categories.find((c) => c.id === cat.ustKategoriId)?.ad
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Sıra</div>
                      <div className="font-medium">{cat.siraNo}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Slug</div>
                      <div className="font-medium break-all">{cat.slug}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Görünür</div>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          cat.aktifMi ? "bg-green-100 text-green-800" : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {cat.aktifMi ? "Evet" : "Hayır"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="shrink-0 flex flex-col gap-1">
                  <button onClick={() => openEditModal(cat)} title="Düzenle" className="p-1.5 border rounded">
                    <Edit2 className="w-4 h-4 text-blue-600" />
                  </button>
                  <button onClick={() => handleDelete(cat.id)} title="Sil" className="p-1.5 border rounded">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Masaüstü tablo (md:block) */}
      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto hidden md:block">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Yükleniyor…</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left uppercase text-xs">Kategori Adı</th>
                <th className="px-3 py-2 text-left uppercase text-xs">Ana Kategori</th>
                <th className="px-3 py-2 text-left uppercase text-xs">Sayfa Bağlantısı</th>
                <th className="px-3 py-2 text-left uppercase text-xs">Sıra</th>
                <th className="px-3 py-2 text-left uppercase text-xs">Görünür</th>
                <th className="px-3 py-2 text-left uppercase text-xs">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-gray-500">Kayıt yok</td>
                </tr>
              ) : (
                rows.map(({ cat, depth }) => (
                  <tr key={cat.id} className="hover:bg-gray-50">
                    {/* Ad (indent) */}
                    <td
                      className="px-3 py-2 font-medium text-gray-900"
                      style={{ paddingLeft: `${(depth ? depth : 0) * 1.25 + 0.75}rem` }}
                    >
                      {cat.ad}
                    </td>

                    {/* Üst Kategori */}
                    <td className="px-3 py-2 text-gray-700">
                      {cat.ustKategoriId
                        ? categories.find((c) => c.id === cat.ustKategoriId)?.ad
                        : "—"}
                    </td>

                    {/* Slug */}
                    <td className="px-3 py-2 text-blue-600 break-all">{cat.slug}</td>

                    {/* Sıra */}
                    <td className="px-3 py-2 text-gray-700">{cat.siraNo}</td>

                    {/* Görünür */}
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          cat.aktifMi ? "bg-green-100 text-green-800" : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {cat.aktifMi ? "Evet" : "Hayır"}
                      </span>
                    </td>

                    {/* İşlemler */}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditModal(cat)} title="Düzenle" className="p-1.5 border rounded hover:bg-white">
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </button>
                        <button onClick={() => handleDelete(cat.id)} title="Sil" className="p-1.5 border rounded hover:bg-white">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-lg w-[95vw] max-w-lg p-4 sm:p-5 md:p-6 relative">
            {/* Kapat */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              aria-label="Kapat"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-lg sm:text-xl font-semibold mb-3">
              {isEditing ? "Kategori Düzenle" : "Yeni Kategori Ekle"}
            </h2>

            <div className="space-y-3">
              {/* Ad */}
              <div>
                <label className="block text-sm font-medium">Kategori Adı</label>
                <input
                  type="text"
                  value={form.ad || ""}
                  onChange={(e) => setForm({ ...form, ad: e.target.value })}
                  placeholder="Ör: Kahve"
                  className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                />
              </div>

              {/* Ana Kategori */}
              <div>
                <label className="block text-sm font-medium">Ana Kategori (Opsiyonel)</label>
                <Select<CategoryOption, false, GroupBase<CategoryOption>>
                  options={categoryOptions}
                  value={categoryOptions.find((o) => o.value === form.ustKategoriId) ?? null}
                  onChange={(opt) => setForm({ ...form, ustKategoriId: opt?.value ?? null })}
                  isClearable
                  placeholder="Ara veya seç…"
                  formatOptionLabel={(
                    option: CategoryOption,
                    _meta: FormatOptionLabelMeta<CategoryOption>
                  ) => (
                    <div style={{ marginLeft: option.depth * 16 }}>{option.label}</div>
                  )}
                  styles={{
                    control: (base) => ({ ...base, marginTop: 4 }),
                    menu: (base) => ({ ...base, zIndex: 60 }),
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Yazmaya başlayarak veya listeden tıklayarak seçin.</p>
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium">Sayfa Bağlantısı (URL)</label>
                <input
                  type="text"
                  value={form.slug || ""}
                  onChange={(e) => {
                    setManualSlug(true);
                    setForm({ ...form, slug: e.target.value });
                  }}
                  placeholder="Otomatik oluşturulur, istersen düzenleyebilirsin"
                  className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Boş bırakırsan, yazdığın ada göre otomatik oluşturulur.
                </p>
              </div>

              {/* Sıra */}
              <div>
                <label className="block text-sm font-medium">Gösterim Sırası</label>
                <input
                  type="number"
                  value={form.siraNo ?? 0}
                  onChange={(e) => setForm({ ...form, siraNo: Number(e.target.value) })}
                  placeholder="0, 1, 2..."
                  className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                />
              </div>

              {/* Görünürlük (sadece düzenlemede) */}
              {isEditing && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!form.aktifMi}
                    onChange={(e) => setForm({ ...form, aktifMi: e.target.checked })}
                  />
                  Bu kategori herkese görünsün mü?
                </label>
              )}
            </div>

            {/* Butonlar */}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={closeModal} className="px-3 sm:px-4 py-2 rounded border hover:bg-gray-100 text-sm">
                Vazgeç
              </button>
              <button
                onClick={handleSave}
                className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KategoriPage;
